const {Component} = require("react");
const {observer} = require("mobx-react");
const env = require("services/env");
const store = require("stores/TeachersStore");
const TeacherCard = require("components/frontend/TeacherCard");
const {classList} = require("helpers/html");
const {queryObject} = require("helpers/object");
const Link = require("components/mixins/Link");
const {isArray} = require("helpers/array");
const moment = require("moment");
const {decodeObservable, pluckKeys} = require("helpers/object");
const Loader = require("components/mixins/Loader");
const Address = require("components/abstract/Address");
const Price = require("components/abstract/Price");
const RestrictedInput = require("components/forms/RestrictedInput");
const Date = require("components/abstract/Date");
const SubjectTags = require("components/widgets/SubjectTags");
const {debounce} = require("helpers/throttle");
const animations = require("modules/animations");

/*
 -------------------------------
 Suffixed input
 -------------------------------
 */

const SuffixedInput = function ({className, suffix, inputRef, ...props}) {
	return (
		<div className={classList("suffixed-input", className)}>
			<input ref={inputRef} {...props} />
			<span className="suffix">{suffix}</span>
		</div>
	);
};

/*
 -------------------------------
 Area address input
 -------------------------------
 */

class AreaAddress extends Address {

	render() {
		return (
			<div className={this.className}>
				<input type="text" ref={this._setInputRef}
							 defaultValue={this.value("name")} {...this.inputProps} />
				{this.renderGuessLocation()}
			</div>
		);
	}

}

/*
 -------------------------------
 Area radius input
 -------------------------------
 */

const AreaRadius = (props) => {
	const normalize = value => {
		return value.length === 0 ? "" : Math.max(0, parseInt(value.replace(/[^0-9]/g, "")));
	};

	return (
		<RestrictedInput className="area-radius" inputComponent={SuffixedInput} normalize={normalize}
										 suffix="km" {...props}/>
	);
};

/*
 -------------------------------
 Price input
 -------------------------------
 */

class MaxPrice extends Price {

	render() {
		return (
			<SuffixedInput className={this.className} inputRef={this._setInputRef} defaultValue={this.state.value}
										 onInput={this._onInput} suffix={this.props.currency} {...this.inputProps}/>
		);
	}

}

/*
 -------------------------------
 Date input
 -------------------------------
 */

class BookingDate extends Date {

	render() {
		return (
			<input ref={this._setInputRef} className={this.className} type="text" {...this.inputProps} />
		);
	}

}

/*
 -------------------------------
 Component
 -------------------------------
 */

@observer
class SearchBox extends Component {

	constructor(props) {
		super(props);

		let area = this.constraint("area", {});

		// convert json encoded empty object
		if (isArray(area)) {
			area = {};
		}

		let levels = this.constraint("levels", []);
		let subjects = this.constraint("subjects", []);
		let date = this.constraint("date"/*, moment().format("YYYY-MM-DD")*/);
		let max_price = this.constraint("max_price");
		let page = this.constraint("$page");
		let seed = this.constraint("seed");

		this.state = {
			loading: false,
			count: 0,
			constraints: {
				levels,
				subjects,
				area,
				date,
				max_price,
				seed,
				'$page': page
			}
		};
	}

	componentDidMount() {
		window.addEventListener('scroll', this.handleScroll);
		this.setState({count: store.collection.length});
	}

	constraint(query, defaultValue = null) {
		return decodeObservable(queryObject(store.constraints, query, defaultValue));
	}

	metadata(query, defaultValue = null) {
		return decodeObservable(queryObject(store.metadata, query, defaultValue));
	}

	pagination(query, defaultValue = null) {
		return queryObject(this.metadata('pagination'), query, defaultValue);
	}

	updateConstraints(constraints = null, loadingState = true, resetPage = true) {
		if (loadingState) {
			this.setState({loading: true});
		}

		constraints = constraints || this.state.constraints;

		if (resetPage) {
			let state = this.state.constraints;
			state['$page'] = 1;
			this.setState(state);
			constraints['$page'] = 1;
		}

		// Remove loading state when done
		return store.constrain(constraints).then(() => {
			if (loadingState) {
				this.setState({loading: false});
			}
		});
	}

	getLevelById(levelId) {
		return env.get("options.levels").filter(level => level.id === levelId) [0];
	}

	/*
	 -------------------------------
	 Accessors
	 -------------------------------
	 */

	get currentLevel() {
		const levelId = this.state.constraints.levels[0];

		if (levelId == undefined) {
			return;
		}

		return this.getLevelById(levelId);
	}

	get subjectsPool() {
		const currentLevel = this.currentLevel;
		const selected = this.state.subjects || [];

		return env.get("options.subjects", []).reduce((acc, subject) => {
			if ((currentLevel.subjects.indexOf(subject.id) !== -1) && (selected.indexOf(subject.id) === -1)) {
				acc.push(subject);
			}

			return acc;
		}, []);
	}

	get subjectsValue() {
		const subjects = env.get("options.subjects", []);

		return (this.state.constraints.subjects || []).reduce((acc, subjectId) => {
			const subject = subjects.filter(entry => entry.id === subjectId) [0];
			if (subject) {
				acc.push(subject);
			}

			return acc;
		}, []);
	}

	get areaAddressValue() {
		return pluckKeys(this.state.constraints.area, ["name", "latitude", "longitude"]);
	}

	get areaRadiusValue() {
		return this.state.constraints.area.radius;
	}

	get locationReference() {
		const {latitude, longitude} = this.state.constraints.area;
		return (typeof latitude === "number") && (typeof longitude === "number") ?
			{latitude, longitude} : null;
	}

	get maxPriceValue() {
		return this.state.constraints.max_price;
	}

	get bookingDateValue() {
		return this.state.constraints.date;// || moment().format("YYYY-MM-DD");
	}

	get page() {
		return this.state.constraints['$page'];
	}

	get pages() {
		if (this.metadata('pagination')) {
			return this.pagination('pages');
		}
		return 1;
	}

	get hasMore() {
		return this.page < this.pages;
	}

	/*
	 -------------------------------
	 Hooks
	 -------------------------------
	 */

	toggleLevel(levelId) {
		return () => {
			this.setState(state => {
				const level = this.getLevelById(levelId);

				// Update current level
				state.constraints.levels = [levelId];

				// Update subjects
				state.constraints.subjects = state.constraints.subjects.filter(subjectId => level.subjects.indexOf(subjectId) !== -1);

				return state;
			});
			this.updateConstraints();
		};
	}

	toggleOrder(order) {
		return () => {
			this.updateConstraints({order}, false);
		};
	}

	toggleSubject(subject) {
		return () => {
			this.updateConstraints(constraints => {
				const subjects = constraints.subjects || [];
				const index = subjects.findIndex(subjectId => parseInt(subjectId) === parseInt(subject));
				index > -1 ? subjects.splice(index, 1) : subjects.push(subject);

				constraints.subjects = subjects;
				return constraints;
			});
		};
	}

	clearSubjects = () => {
		this.updateConstraints(constraints => {
			delete constraints.subjects;
			return constraints;
		});
	};

	changeSubjects = (subjects) => {
		this.setState(state => {
			state.constraints.subjects = subjects.map(subject => subject.id);
			return state;
		});
	};

	changeArea = (value, callback = null) => {
		this.setState(state => {
			const area = state.constraints.area || {};

			if (value == undefined) {
				delete area.name;
				delete area.latitude;
				delete area.longitude;
			}
			else {
				area.name = value.name;
				area.latitude = value.latitude;
				area.longitude = value.longitude;
			}

			state.constraints.area = area;

			return state;
		}, callback);
	};

	changeRadius = (value) => {
		this.setState(state => {
			state.constraints.area.radius = value;
			return state;
		});
	};

	changeMaxPrice = (value) => {
		this.setState(state => {
			state.constraints.max_price = value;
			return state;
		});
	};

	handleScroll = (e) => {
		const el = this.element;
		const bottom = (window.innerHeight - el.getBoundingClientRect().bottom + 500) >= 0;

		if (bottom) {
			this.loadMore();
		}
	}

	loadMore = debounce(500, () => {
		const winPos = window.scrollY;

		if (!this.state.loading && this.hasMore) {
			this.setState({loading: true});

			const nextPage = this.page + 1;

			store.append({'$page': nextPage})
				.then(() => {
					let {constraints} = this.state;
					constraints['$page'] = nextPage;
					this.setState({
						loading: false,
						count: store.collection.length,
						constraints: constraints,
					}, () => window.scrollTo(0, winPos));
				});
		}
	});

	changeBookingDate = (value) => {
		this.setState(state => {
			state.constraints.date = value;
			return state;
		});
	};

	updateSearch = (e) => {
		e.preventDefault();
		e.stopPropagation();
		this.updateConstraints();
	};

	guessFormat = (place) => {
		if (place.address_components != undefined) {
			for (let i = 0; i < place.address_components.length; i++) {
				if (place.address_components[i].types.indexOf("administrative_area_level_1") !== -1) {
					return place.address_components[i].long_name;
				}

				if (place.address_components[i].types.indexOf("administrative_area_level_2") !== -1) {
					return place.address_components[i].long_name;
				}
			}
		}
	};

	/*
	 -------------------------------
	 Render
	 -------------------------------
	 */

	render() {
		return (
			<div ref={el => this.element = el} className={classList("search-box", this.props.className, this.state.loading ? "loading" : false)}>
				{this.renderFilters()}
				{this.renderItems()}
				<div className="teacher-box-filler"></div>
				<div className="teacher-box-filler"></div>
				<Loader active={this.state.loading}/>
			</div>
		);
	}

	/////////////////////////////////
	// Items
	/////////////////////////////////

	renderItems() {
		const locationRef = this.locationReference;
		const levelRef = this.state.constraints.levels[0];
		let markup = [];
		let order = this.page > 1 ?
			0 - this.state.count :
			0;

		store.collection.forEach((item, index) => {
			order++;
			let delay = order < 0 ? 0 : order;

			markup.push(<TeacherCard key={index} order={delay} locationRef={locationRef} levelRef={levelRef} {...item}/>);
		});

		return markup;
	}

	/////////////////////////////////
	// Filters
	/////////////////////////////////

	renderFilters() {
		return (
			<div className="search-teacher">

				<div className="top-bar animate slide-in">{this.renderLevelsFilter()}</div>

				<form className="animate slide-in" method="post" onSubmit={this.updateSearch}>
					<SubjectTags placeholder="Fag" pool={this.subjectsPool} value={this.subjectsValue}
											 handleChange={this.changeSubjects}/>

					<div className={classList("btns", this.state.loading ? "loading" : false)}>
						<input type="submit" value="Find underviser" className="btn noTags"/>
						<input type="submit" value="" className="btn mobileSearchBtn"/>
						<Loader active={this.state.loading}/>
					</div>

					<div className="input-tag-wrap">
						<div className="maps-autocomplete-wrap">
							<AreaAddress defaultValue={this.areaAddressValue} handleChange={this.changeArea}
													 guessFormat={this.guessFormat}
													 componentRestrictions={{country: "dk"}} placeholder="By"/>
						</div>
						<AreaRadius defaultValue={this.areaRadiusValue} handleChange={this.changeRadius}/>
						<div className="date-picker-wrap">
							<BookingDate value={this.bookingDateValue} min={moment().format("YYYY-MM-DD")} format="D MMMM"
													 mask={false}
													 handleChange={this.changeBookingDate}/>
						</div>
						<MaxPrice defaultValue={this.maxPriceValue} handleChange={this.changeMaxPrice}/>

						<div className={classList("submit-btn", this.state.loading ? "loading" : false)}>
							<input type="submit" className="btn" value="Find underviser"/>
							<Loader active={this.state.loading}/>
						</div>
					</div>
				</form>

				<div className="filter-bar animate fade-in">
					{this.renderResultsFilters()}
					{this.renderOrderFilters()}
				</div>
			</div>
		);
	}

	/////////////////////////////////
	// Filter: levels
	/////////////////////////////////

	renderLevelsFilter() {
		const levelOptions = env.get("options.levels", []);
		const activeLevels = this.state.constraints.levels;
		const active = level => activeLevels.indexOf(level.id) > -1;

		return levelOptions.map(level => {
			const className = active(level) ? "active" : null;

			return (
				<Link className={className} key={level.id}
							onClick={this.toggleLevel(level.id)}>{level.name}{level.beta ? " (Beta)" : null}</Link>
			);
		});
	}

	/////////////////////////////////
	// Filter: order
	/////////////////////////////////

	renderOrderFilters() {
		const order = this.constraint("order", "popularity");
		const className = currentOrder => currentOrder === order ? "active" : "";
		const area = this.constraint("area");

		const proximityMarkup = area && area.latitude && area.longitude && area.radius ?
			(<Link className={className("proximity")} onClick={this.toggleOrder("proximity")}>Tættest på</Link>) :
			null;

		return (
			<div className="filters">
				<Link className={className("popularity")} onClick={this.toggleOrder("popularity")}>Mest populære</Link>
				<Link className={className("rating")} onClick={this.toggleOrder("rating")}>Højst bedømte</Link>
				{proximityMarkup}
				<Link className={className("price")} onClick={this.toggleOrder("price")}>Laveste pris</Link>
			</div>
		);
	}

	/////////////////////////////////
	// Filter: tags (results)
	/////////////////////////////////

	renderTagsFilters() {
		const subjects = this.constraint("subjects", []);
		const name = subjectId => (env.get("options.subjects").filter(entry => entry.id === subjectId) [0]).name;

		if (subjects.length === 0) {
			return;
		}

		const tagsMarkup = subjects.map(subject => {
			return (<div key={subject} onClick={this.toggleSubject(subject)}
									 className="tag colored removable">{name(subject)}</div>);
		});

		return (
			<div className="tag-wrap">
				<div className="tags">{tagsMarkup}</div>
				<Link onClick={this.clearSubjects}>Nulstil</Link>
			</div>
		);
	}

	renderResultsFilters() {
		const subjects = this.constraint("subjects", []);
		const count = this.pagination('total');
		const what = count === 1 ? 'underviser' : 'undervisere';

		const messageMarkup = subjects.length > 0 ?
			(<p>Vi fandt <span className="result-count">{count}</span> der underviser i:</p>) :
			(<p>Vi fandt <span className="result-count">{count}</span> <span>{what}</span>.</p>);

		return (
			<div className="tag-container">
				{messageMarkup}
				{this.renderTagsFilters()}
			</div>
		);
	}

}

module.exports = SearchBox;



// WEBPACK FOOTER //
// ./resources/js/components/frontend/SearchBox.js